/**
 * Implements various behavior policy algorithms, such as session definition,
 * roll-up and purge of old events, compression, and obfuscation.
 *
 * E.g. isInSameSession determines if two events are in the same session
 * using this definition of a session: A set of events ordered on
 * timestamp where the interval between two successive events < 30 minutes.
 *
 * TODO(ajith): Implement roll-up and purge of old events.
 * TODO(ajith): Compress/de-compress events.
 * TODO(ajith): Obfuscate event schema.
 *
 * Ported from [behavior_policy@ab9709a52d](https://github.com/optimizely/optimizely/blob/ab9709a52d9b2579c72d436556b1d2c3c4276a6c/src/www/js/client/behavior_policy.js)
 * @module
 */
var BehaviorEvent = require('./event').Event;
var DateEnv = require('../env/date');
var enums = require('./enums');
var EventBase = require('./event').EventBase;

/**
 * The session index that refers to the current session.
 * Each prior session has a progressively incremented session index, e.g. 1 for the prior session,
 * 2 for the session before that, etc.
 * @type {number}
 * @const
 */
exports.CURRENT_SESSION_INDEX = 0;

/**
 * Milliseconds in 30 minutes.
 * @type {number}
 * @const
 * @private
 */
var MILLIS_IN_30_MINS = 30 * 60 * 1000;

/**
 * Reports whether events are in the same session.
 * It is important to pass successive events, sorted by timestamp, to this
 * function as it simply checks whether the time interval between the events
 * is < 30 minutes.
 * @param {module:behavior/event~Event} e1 First event.
 * @param {module:behavior/event~Event} e2 Second event.
 * @returns {boolean} Whether e1 and e2 are in the same session.
 * @private
 */
exports.isInSameSession = function(e1:import('core/behavior/event').Event, e2:import('core/behavior/event').Event): boolean {
  var t1 = /** @type{number} */(e1.getValueOrDefault([enums.FIELDS.TIME], 0));
  var t2 = /** @type{number} */(e2.getValueOrDefault([enums.FIELDS.TIME], 0));
  return this.isTimestampInSameSession(t1, t2);
};

exports.isTimestampInSameSession = function(t1:number, t2:number): boolean {
  return Math.abs(t1 - t2) < MILLIS_IN_30_MINS;
};

/**
 * Updates the session id of event based on whether it belongs to the same
 * session as lastEventInSession.
 * The session id is the timestamp of the first event in the session.
 * IMPORTANT: This method does *not* guard against a lastEventInSession object
 * that is missing a session id or timestamp field, and must be called with
 * an object that contains these fields.
 * @param {module:behavior/event~Event} lastEventInSession The last event in the session.
 * @param {module:behavior/event~Event} event BehaviorEvent whose session id to update.
 * @private
 */
function updateSessionId_unguarded(lastEventInSession:import('core/behavior/event').Event, event:import('core/behavior/event').Event) {
  var sessionId;
  if (exports.isInSameSession(lastEventInSession, event)) {
    sessionId = lastEventInSession.getValueOrDefault([enums.FIELDS.SESSION_ID]);
  } else {
    sessionId = event.getValueOrDefault([enums.FIELDS.TIME]);
  }
  event.setFieldValue(enums.FIELDS.SESSION_ID, sessionId);
}

/**
 * Updates the session id of event based on whether it belongs to the same
 * session as lastEventInSession.
 * @param {module:behavior/event~Event} lastEventInSession The last event in the session.
 * @param {module:behavior/event~Event} event BehaviorEvent whose session id to update.
 */
exports.updateSessionId = function(lastEventInSession:import('core/behavior/event').Event, event:import('core/behavior/event').Event) {
  if (!lastEventInSession) {
    event.setFieldValue(enums.FIELDS.SESSION_ID, event.getValueOrDefault([enums.FIELDS.TIME]));
    return;
  }

  // Ensure that the last event in session has a timestamp and session id.
  var lastTime = lastEventInSession.getValueOrDefault([enums.FIELDS.TIME]),
      lastSessionId = lastEventInSession.getValueOrDefault([enums.FIELDS.SESSION_ID]);
  var eventTime = /** @type{number} */(event.getValueOrDefault([enums.FIELDS.TIME]));
  // Set default timestamp of last event so that we get a new session id for event.
  lastTime = (typeof lastTime !== 'number' ? eventTime - 2 * MILLIS_IN_30_MINS : lastTime);
  lastSessionId = (typeof lastSessionId !== 'number' ? lastTime : lastSessionId);
  // Do not modify input last event; create an object with just timestamp and session id.
  lastEventInSession = new BehaviorEvent(new EventBase('', ''), lastTime, lastSessionId);
  updateSessionId_unguarded(lastEventInSession, event);
};

/**
 * Updates the session index of event based on whether it belongs to the same
 * session as the previous event.
 * IMPORTANT: This method does *not* guard against a previousEvent object
 * that is missing a session index or timestamp field, and must be called with
 * an object that contains these fields.
 * @param {module:behavior/event~Event} previousOrNextEvent Previous or next event.
 * @param {module:behavior/event~Event} currentEvent Current event to assign a session index.
 * @param {boolean} next The first event passed in, on which to base the session index, might be either the more
 * recent event (if called from sessionize, next is true) or the previous event (if called from makeEvent, next is false).
 * @private
 */

function updateSessionIndex_unguarded(previousOrNextEvent:import('core/behavior/event').Event, currentEvent:import('core/behavior/event').Event, next:boolean) {
  var sessionIndex;
  var previousOrNextSessionIndex = previousOrNextEvent.getValueOrDefault([enums.FIELDS.SESSION_INDEX]);
  if (exports.isInSameSession(currentEvent, previousOrNextEvent)) {
    sessionIndex = previousOrNextSessionIndex;
  } else if (next) {
    // event on which to base the session index is the more recent one
    sessionIndex = previousOrNextSessionIndex + 1;
  } else {
    sessionIndex = previousOrNextSessionIndex - 1;
  }
  currentEvent.setFieldValue(enums.FIELDS.SESSION_INDEX, sessionIndex);
}

/**
 * Updates the session index of event based on whether it belongs to the same
 * session as lastEventInSession.
 * @param {module:behavior/event~Event} lastEventInSession The last event in the session.
 * @param {module:behavior/event~Event} event BehaviorEvent whose session id to update.
 */
exports.updateSessionIndex = function(lastEventInSession:import('core/behavior/event').Event, event:import('core/behavior/event').Event) {
  if (!lastEventInSession) {
    event.setFieldValue(enums.FIELDS.SESSION_INDEX, 0);
    return;
  }

  // Ensure that the last event in session has a timestamp and session index.
  var lastTime = lastEventInSession.getValueOrDefault([enums.FIELDS.TIME]);
  var lastSessionIndex = lastEventInSession.getValueOrDefault([enums.FIELDS.SESSION_INDEX]);
  var eventTime = /** @type{number} */(event.getValueOrDefault([enums.FIELDS.TIME]));
  var lastSessionId = lastEventInSession.getValueOrDefault([enums.FIELDS.SESSION_ID]);
  // Set default timestamp of last event
  lastTime = (typeof lastTime !== 'number' ? eventTime - 2 * MILLIS_IN_30_MINS : lastTime);
  // Not sure what this default should be since we can't just get it from the timestamp
  // Why would some events not have timestamps/sessionIds?
  lastSessionIndex = (typeof lastSessionIndex !== 'number' ? 0 : lastSessionIndex);
  lastSessionId = (typeof lastSessionId !== 'number' ? lastTime : lastSessionId);
  // Do not modify input last event; create an object with just timestamp and session id.
  lastEventInSession = new BehaviorEvent(new EventBase('', ''), lastTime, lastSessionId, lastSessionIndex);
  updateSessionIndex_unguarded(lastEventInSession, event, false);
};

/**
 * Sessionizes events using the session definition implemented by
 * isInSameSession. Each event will have a session id; all events with the
 * same session id belong to the same session.
 * Precondition: events must be sorted on timestamp.
 * @param {module:behavior/event~Event[]} events Events to sessionize.
 */
exports.sessionize = function(events:import('core/behavior/event').Event[]) {
  var numEvents = events.length;
  if (numEvents === 0) {
    return;
  }

  events[0].setFieldValue(enums.FIELDS.SESSION_ID,
    events[0].getValueOrDefault([enums.FIELDS.TIME]));
  for (var i = 1; i < numEvents; i++) {
    updateSessionId_unguarded(events[i - 1], events[i]);
  }

  // Set last (most recent) event to current session or previous session.
  var startingIndex = exports.CURRENT_SESSION_INDEX;
  var lastTime = events[numEvents - 1].getValueOrDefault([enums.FIELDS.TIME]);
  var currentTime = DateEnv.now();
  if (currentTime - lastTime > MILLIS_IN_30_MINS) {
    startingIndex += 1;
  }
  events[numEvents - 1].setFieldValue(enums.FIELDS.SESSION_INDEX, startingIndex);

  // Set sesssion index for each event based on the more recent event.
  for (var i = numEvents - 1; i > 0; i--) {
    updateSessionIndex_unguarded(events[i], events[i - 1], true);
  }
};

/**
 * When a new event occurs after 30 minutes of inactivity, a new session starts, so we need to reindex the existing
 * events in the store.
 * @param {module:behavior/event~Event} lastEvent Event that was just added
 * @param {module:behavior/event~Event[]} events Events in the store.
 * @param {module:behavior/event~Event[]} eventQueue Event queue.
 */
exports.reindexIfNecessary = function(lastEvent:import('core/behavior/event').Event, events:import('core/behavior/event').Event[], eventQueue:import('core/behavior/event').Event[]) {
  function reindex(evs) {
    for (var i = 0; i < evs.length; i++) {
      var currentIndex = evs[i].getValueOrDefault([enums.FIELDS.SESSION_INDEX]);
      evs[i].setFieldValue(enums.FIELDS.SESSION_INDEX, currentIndex + 1);
    }
  }

  if (lastEvent.getValueOrDefault([enums.FIELDS.SESSION_INDEX]) === -1) {
    // This means events need to be reindexed (new session started between second to last event and event just added)
    reindex(events);
    reindex(eventQueue);
  }
};

/**
 * A predicate for use when sorting events for sessionization.
 * @param {module:behavior/event~Event} a
 * @param {module:behavior/event~Event} b
 * @returns {number}
 */
exports.sessionSortPredicate = function(a:import('core/behavior/event').Event, b:import('core/behavior/event').Event): number {
  return a[enums.FIELDS.TIME] - b[enums.FIELDS.TIME];
};

/**
 * Applies any required migrations.
 * @param {module:behavior/event~EventBase[]} events Events to migrate.
 * @returns {boolean} Whether a migration was applied.
 */
exports.applyMigrations = function(events:import('core/behavior/event').EventBase[]): boolean {
  return false;
};

if (__TEST__) {
  exports.MILLIS_IN_30_MINS = MILLIS_IN_30_MINS;
  exports.updateSessionIndex_unguarded = updateSessionIndex_unguarded;
}
